import { IAttachment } from '@app/models/Attachment'
import { AttachmentType } from '@app/models/enums'
import { IFileUploadAttachment } from '@app/models/FileUploadAttachment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as React from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import * as FileUploadState from '../modules/fileUpload'

interface IComponentProps {
  dataSource?: IAttachment | List<IAttachment>
  identifier: string
  mainPhotoId?: string
  onResize?: () => void
  onSetMainPhoto?: (attachmentId?: string) => void
}

interface IComponentState {
  identifier: string
}

type ComponentProps = IComponentProps &
  FileUploadState.IFileUploadState &
  typeof FileUploadState.actionCreators

export default class FileProgress extends React.PureComponent<
  ComponentProps,
  IComponentState
> {
  private containerRef: React.RefObject<HTMLDivElement>
  private clientHeight: number

  public constructor(props: ComponentProps) {
    super(props)

    this.containerRef = React.createRef()
    this.clientHeight = 0

    this.state = {
      identifier: this.props.identifier,
    }
  }

  public componentDidMount() {
    if (this.containerRef.current) {
      this.clientHeight = this.containerRef.current.clientHeight
    }

    if (this.props.dataSource) {
      this.props.loadExistingAttachments(
        this.state.identifier,
        List.isList(this.props.dataSource)
          ? (this.props.dataSource as List<IAttachment>)
          : List([this.props.dataSource as IAttachment]),
      )
    }
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.containerRef.current) {
      if (this.containerRef.current.clientHeight !== this.clientHeight) {
        this.clientHeight = this.containerRef.current.clientHeight
        if (this.props.onResize) {
          this.props.onResize()
        }
      }
    }

    if (
      this.props.dataSource !== prevProps.dataSource &&
      this.props.dataSource
    ) {
      this.props.loadExistingAttachments(
        this.state.identifier,
        List.isList(this.props.dataSource)
          ? (this.props.dataSource as List<IAttachment>)
          : List([this.props.dataSource as IAttachment]),
      )
    }
  }

  public componentWillUnmount() {
    this.props.clearFileList(this.props.identifier)
  }

  public render() {
    if (this.props.files.size === 0) {
      return null
    }

    const filesToRender = this.props.files.get(this.state.identifier)
    if (filesToRender) {
      const progressItems = filesToRender.toArray().map((item) => {
        if (!item) {
          return null
        }

        const titleStyle: React.CSSProperties = {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 100,
        }

        if (item && item.attachment && item.attachment.baseStorageUrl) {
          titleStyle.flexGrow = 1
        }

        if (
          (item && item.progress > 0) ||
          (item.attachment && item.attachment.baseStorageUrl)
        ) {
          return (
            <div key={item.id} className='d-flex flex-row mt-2'>
              <div className='mr-2'>
                {this._isPhoto(item.attachment) && (
                  <img src={item.file.preview} height={30} width={30} />
                )}
                {item.attachment.type === AttachmentType.Document && (
                  <FontAwesomeIcon
                    icon={['fal', 'file']}
                    style={{ width: 30, fontSize: '16px' }}
                  />
                )}
                {item.attachment.type === AttachmentType.NotSpecified && (
                  <FontAwesomeIcon
                    icon={['fal', 'file']}
                    style={{ width: 30, fontSize: '16px' }}
                  />
                )}
                {item.attachment.type === AttachmentType.Video && (
                  <FontAwesomeIcon
                    icon={['fal', 'video']}
                    style={{ width: 30, fontSize: '16px' }}
                  />
                )}
              </div>
              <div className='mr-2' style={titleStyle}>
                <span>{item.attachment.title || item.file.name}</span>
              </div>

              {this.renderProgress(item)}

              <div className='text-right'>
                {this._isPhoto(item.attachment) &&
                  this.props.onSetMainPhoto !== undefined && (
                    <span className='mr-2'>
                      {item.attachment.id !== undefined &&
                      item.attachment.id === this.props.mainPhotoId ? (
                        <span className='small'>Main Photo</span>
                      ) : (
                        <a
                          className='text-primary'
                          onClick={() => this._onSetMainPhoto(item.attachment)}
                          title='Set as main photo'
                        >
                          <FontAwesomeIcon icon={['fal', 'image']} fixedWidth />
                        </a>
                      )}
                    </span>
                  )}
                <a
                  className={`mr-2 text-primary ${
                    item.attachment && item.attachment.id ? '' : 'linkDisabled'
                  }`}
                  onClick={(e) => {
                    if (item.attachment && item.attachment.id) {
                      this.props.enableEditFileItem(item)
                    }
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
                </a>

                <a
                  className='text-danger'
                  onClick={(e) => this.confirmDelete(item)}
                >
                  <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                </a>
              </div>
              {this.renderDeleteModal(item)}
            </div>
          )
        } else {
          return null
        }
      })

      return (
        <div className='m-2' ref={this.containerRef}>
          {progressItems}
        </div>
      )
    } else {
      return null
    }
  }

  private _isPhoto(attachment: IAttachment) {
    return (
      attachment.type === AttachmentType.MainPhoto ||
      attachment.type === AttachmentType.Photo
    )
  }

  private _onSetMainPhoto(attachment: IAttachment) {
    if (this.props.onSetMainPhoto !== undefined && attachment.id) {
      this.props.onSetMainPhoto(attachment.id)
    }
  }

  private _onDeleteConfirmed(item: IFileUploadAttachment) {
    this.props.removeFileUploadItem(item.key, item.id)

    // If the mainPhoto is deleted, we need to select another photo from the list
    if (
      this.props.onSetMainPhoto !== undefined &&
      item.attachment.id === this.props.mainPhotoId
    ) {
      const filesToRender = this.props.files.get(this.state.identifier)
      const file = filesToRender
        .toArray()
        .find((f) => f.attachment.id !== item.attachment.id)
      if (file !== undefined) {
        this.props.onSetMainPhoto(file.attachment.id)
      } else {
        this.props.onSetMainPhoto(undefined)
      }
    }
  }

  private renderProgress(item: any) {
    let displayProgress = false
    if (item.attachment.dateCreated === 0 && item.progress < 100) {
      displayProgress = true
    }

    if (item.error) {
      displayProgress = true
    }

    if (displayProgress) {
      return (
        <div className='progress mr-2 mt-1' style={{ flex: 1 }}>
          <div
            className={`progress-bar ${
              item.progress < 100
                ? 'progress-bar-striped progress-bar-animated'
                : ''
            }
        ${item.error ? 'bg-danger' : ''}`}
            role='progressbar'
            style={{ width: `${item.progress}%` }}
            aria-valuenow={item.progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >{`${item.progress}%`}</div>
        </div>
      )
    } else {
      return null
    }
  }

  private renderDeleteModal(item: any) {
    return (
      <Modal
        isOpen={item.isConfirmDeleteModalOpen}
        toggle={() => this.toggle(item)}
      >
        <ModalHeader toggle={() => this.toggle(item)}>
          Confirm Remove File
        </ModalHeader>
        <ModalBody>
          Are you sure you want to remove the file &quot;
          {item.attachment.title || item.file.name}&quot;?
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => this.toggle(item)}>
            No
          </Button>
          <Button color='primary' onClick={() => this._onDeleteConfirmed(item)}>
            Yes
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private toggle(item: any) {
    this.props.toggleConfirmDeleteModal(item)
  }

  private confirmDelete(item: any) {
    this.props.toggleConfirmDeleteModal(item)
  }
}
